import type {
  AppConfigRow,
  InventorySupplierRow,
  PurchaseOrderLineRow,
  PurchaseOrderPrintSettingsRow,
  PurchaseOrderRow,
} from '@helios/data-access';
import {
  resolveBuyer,
  resolvePurchaseOrderSettings,
  resolveSupplier,
} from '../clinic/purchase-order-print/print-utils';

/* ------------------------------------------------------------------ */
/*  PRD.241 §9–10 — Build the supplier-facing PO email payload.       */
/*                                                                    */
/*  Pure function. No DB access; no React. Caller supplies the PO     */
/*  data (already fetched by the modal via PowerSync). Returns the    */
/*  full body the user reviews + the subject + the default recipient. */
/*                                                                    */
/*  Snapshot-first rules (PRD.241 §12):                               */
/*    - Supplier name: PO.supplier_name snapshot first.               */
/*    - Item / SKU / unit / rate / amount: line snapshots first.      */
/*    - Live supplier master email allowed for delivery routing       */
/*      because email is operational contact data.                    */
/* ------------------------------------------------------------------ */

export interface BuildPurchaseOrderEmailInput {
  po: PurchaseOrderRow;
  lines: PurchaseOrderLineRow[];
  supplierMaster: InventorySupplierRow | null;
  appConfig: AppConfigRow | null;
  savedSettings: PurchaseOrderPrintSettingsRow | null;
  /**
   * Optional editable intro paragraph from the modal. When omitted, a
   * sensible default greeting + closing is generated.
   */
  introMessage?: string;
  /** Currency formatter — caller-supplied so locale stays consistent. */
  formatMoney: (amount: number) => string;
}

export interface BuildPurchaseOrderEmailResult {
  subject: string;
  text: string;
  html: string;
  /** Best-guess recipient from supplier master, or null when unavailable. */
  defaultRecipient: { email: string; name: string } | null;
  /** Buyer display name used in greeting/signature. */
  buyerName: string;
  /** Snapshot-first supplier display name. */
  supplierName: string;
  /** PO number for the modal header. */
  poNumber: string;
}

/**
 * Build the email subject + text + html bodies from PO data.
 *
 * No PDF attachment is generated — v1 ships the document inside the body
 * per PRD.241 §5. The HTML body is hand-rolled inline-styled markup so
 * it renders without external CSS in email clients.
 */
export function buildPurchaseOrderEmail(
  input: BuildPurchaseOrderEmailInput,
): BuildPurchaseOrderEmailResult {
  const { po, lines, supplierMaster, appConfig, savedSettings, formatMoney } = input;

  const settings = resolvePurchaseOrderSettings(savedSettings);
  const buyer = resolveBuyer(settings, appConfig);
  const supplier = resolveSupplier(po.supplier_name, supplierMaster);

  const poNumber = po.po_number?.trim() || po.id.slice(0, 8);
  const poDate = formatDate(po.po_date ?? po.created_at);
  const amountsAreLabel = formatAmountsAre(po.amounts_are);
  const statusLabel = po.status ? capitalise(po.status) : '';

  const subtotal = lines.reduce((sum, l) => sum + (l.amount ?? 0), 0);
  const totalAmount = po.total_amount ?? subtotal;

  // PRD.241 §9 — default subject keyed on PO number + clinic name. We
  // fall back gracefully when PO number is missing.
  const subject = poNumber
    ? `Purchase Order ${poNumber} from ${buyer.name}`
    : `Purchase Order from ${buyer.name}`;

  const defaultIntro =
    input.introMessage?.trim() ||
    `Dear ${supplier.name},\n\nPlease see our purchase order details below.\n\nThank you,\n${buyer.name}`;

  const text = buildText({
    intro: defaultIntro,
    buyer,
    supplier,
    poNumber,
    poDate,
    amountsAreLabel,
    statusLabel,
    lines,
    subtotal,
    totalAmount,
    notes: po.notes ?? '',
    settings,
    formatMoney,
  });

  const html = buildHtml({
    intro: defaultIntro,
    buyer,
    supplier,
    poNumber,
    poDate,
    amountsAreLabel,
    statusLabel,
    lines,
    subtotal,
    totalAmount,
    notes: po.notes ?? '',
    settings,
    formatMoney,
  });

  // PRD.241 §11 — Supplier master email is allowed as operational contact;
  // snapshot supplier name kept for display.
  const supplierEmail = supplierMaster?.email?.trim() || '';
  const defaultRecipient = supplierEmail ? { email: supplierEmail, name: supplier.name } : null;

  return {
    subject,
    text,
    html,
    defaultRecipient,
    buyerName: buyer.name,
    supplierName: supplier.name,
    poNumber,
  };
}

// ─── Internals ───────────────────────────────────────────────────────

interface BuildArgs {
  intro: string;
  buyer: ReturnType<typeof resolveBuyer>;
  supplier: ReturnType<typeof resolveSupplier>;
  poNumber: string;
  poDate: string;
  amountsAreLabel: string;
  statusLabel: string;
  lines: PurchaseOrderLineRow[];
  subtotal: number;
  totalAmount: number;
  notes: string;
  settings: ReturnType<typeof resolvePurchaseOrderSettings>;
  formatMoney: (amount: number) => string;
}

function buildText(a: BuildArgs): string {
  const lineRows = a.lines.length
    ? a.lines
        .map((l, i) => {
          const item = l.item_name_snapshot?.trim() || '—';
          const sku = a.settings.showSku && l.sku_snapshot ? ` (SKU ${l.sku_snapshot})` : '';
          const qty = l.quantity_ordered ?? 0;
          const unit = l.unit_snapshot ? ` ${l.unit_snapshot}` : '';
          const bonus =
            a.settings.showBonus && (l.bonus_quantity ?? 0) > 0
              ? ` +${l.bonus_quantity} bonus`
              : '';
          const rate = l.rate != null ? ` @ ${a.formatMoney(l.rate)}` : '';
          const amount = l.amount != null ? ` = ${a.formatMoney(l.amount)}` : '';
          const remarks =
            a.settings.showRemarks && l.remarks?.trim() ? `\n    Note: ${l.remarks.trim()}` : '';
          return `${i + 1}. ${item}${sku}\n    ${qty}${unit}${bonus}${rate}${amount}${remarks}`;
        })
        .join('\n')
    : '(no line items)';

  const supplierContactBlock =
    a.settings.showSupplierContact &&
    (a.supplier.address || a.supplier.phone || a.supplier.email || a.supplier.taxId)
      ? `\n${a.supplier.address ? a.supplier.address + '\n' : ''}${a.supplier.phone ? 'Tel: ' + a.supplier.phone + '\n' : ''}${a.supplier.email ? a.supplier.email + '\n' : ''}${a.supplier.taxId ? 'Tax ID: ' + a.supplier.taxId + '\n' : ''}`
      : '';

  const termsBlock =
    a.settings.showTerms && a.settings.termsText.trim()
      ? `\n\nTerms & Conditions:\n${a.settings.termsText.trim()}`
      : '';

  const notesBlock = a.notes.trim() ? `\n\nNotes:\n${a.notes.trim()}` : '';

  const footerBlock = a.settings.footerText.trim() ? `\n\n${a.settings.footerText.trim()}` : '';

  return [
    a.intro,
    '',
    '─'.repeat(40),
    'PURCHASE ORDER',
    '─'.repeat(40),
    `PO Number: ${a.poNumber}`,
    `PO Date:   ${a.poDate}`,
    a.statusLabel ? `Status:    ${a.statusLabel}` : '',
    `Amounts:   ${a.amountsAreLabel}`,
    '',
    `From: ${a.buyer.name}`,
    a.buyer.address ?? '',
    a.buyer.phone ? `Tel: ${a.buyer.phone}` : '',
    a.buyer.email ?? '',
    '',
    `To:   ${a.supplier.name}${supplierContactBlock}`,
    '',
    'Line Items:',
    lineRows,
    '',
    `Subtotal:     ${a.formatMoney(a.subtotal)}`,
    `Total Amount: ${a.formatMoney(a.totalAmount)} (${a.amountsAreLabel.toLowerCase()})`,
    notesBlock,
    termsBlock,
    footerBlock,
  ]
    .filter((line) => line !== '')
    .join('\n');
}

function buildHtml(a: BuildArgs): string {
  const accent = '#5A2A18';
  const muted = '#475569';
  const border = '#e2e8f0';

  const introHtml = a.intro
    .split('\n')
    .map((p) => `<p style="margin:0 0 12px 0;line-height:1.5;">${escapeHtml(p)}</p>`)
    .join('');

  const supplierContactHtml =
    a.settings.showSupplierContact &&
    (a.supplier.address || a.supplier.phone || a.supplier.email || a.supplier.taxId)
      ? `<div style="color:${muted};font-size:12px;line-height:1.5;margin-top:4px;white-space:pre-line;">${[
          a.supplier.address,
          a.supplier.phone ? `Tel: ${a.supplier.phone}` : '',
          a.supplier.email,
          a.supplier.taxId ? `Tax ID: ${a.supplier.taxId}` : '',
        ]
          .filter((v): v is string => !!v && v.length > 0)
          .map(escapeHtml)
          .join('\n')}</div>`
      : '';

  const showSku = a.settings.showSku;
  const showBonus = a.settings.showBonus && a.lines.some((l) => (l.bonus_quantity ?? 0) > 0);
  const showRemarks = a.settings.showRemarks && a.lines.some((l) => (l.remarks ?? '').trim());

  const headRow = `
    <tr style="background:${accent};color:#ffffff;">
      <th style="padding:8px 6px;text-align:left;font-weight:600;font-size:11px;width:24px;">#</th>
      <th style="padding:8px 6px;text-align:left;font-weight:600;font-size:11px;">Item</th>
      ${showSku ? `<th style="padding:8px 6px;text-align:left;font-weight:600;font-size:11px;">SKU</th>` : ''}
      <th style="padding:8px 6px;text-align:left;font-weight:600;font-size:11px;">Unit</th>
      <th style="padding:8px 6px;text-align:right;font-weight:600;font-size:11px;">Qty</th>
      ${showBonus ? `<th style="padding:8px 6px;text-align:right;font-weight:600;font-size:11px;">Bonus</th>` : ''}
      <th style="padding:8px 6px;text-align:right;font-weight:600;font-size:11px;">Rate</th>
      <th style="padding:8px 6px;text-align:right;font-weight:600;font-size:11px;">Amount</th>
    </tr>
  `;

  const bodyRows = a.lines.length
    ? a.lines
        .map((l, i) => {
          const item = l.item_name_snapshot?.trim() || '—';
          const sku = l.sku_snapshot ?? '—';
          const unit = l.unit_snapshot ?? '—';
          const qty = l.quantity_ordered ?? 0;
          const bonus = (l.bonus_quantity ?? 0) > 0 ? String(l.bonus_quantity) : '—';
          const rate = l.rate != null ? a.formatMoney(l.rate) : '—';
          const amount = l.amount != null ? a.formatMoney(l.amount) : '—';
          const remarks =
            showRemarks && l.remarks?.trim()
              ? `<div style="color:${muted};font-size:11px;margin-top:2px;">${escapeHtml(l.remarks.trim())}</div>`
              : '';
          return `
            <tr style="border-top:1px solid ${border};">
              <td style="padding:8px 6px;text-align:center;font-size:12px;vertical-align:top;">${i + 1}</td>
              <td style="padding:8px 6px;font-size:12px;vertical-align:top;">
                <div style="font-weight:600;">${escapeHtml(item)}</div>
                ${remarks}
              </td>
              ${showSku ? `<td style="padding:8px 6px;font-size:12px;vertical-align:top;">${escapeHtml(sku)}</td>` : ''}
              <td style="padding:8px 6px;font-size:12px;vertical-align:top;">${escapeHtml(unit)}</td>
              <td style="padding:8px 6px;text-align:right;font-size:12px;vertical-align:top;">${escapeHtml(String(qty))}</td>
              ${showBonus ? `<td style="padding:8px 6px;text-align:right;font-size:12px;vertical-align:top;">${escapeHtml(bonus)}</td>` : ''}
              <td style="padding:8px 6px;text-align:right;font-size:12px;vertical-align:top;">${escapeHtml(rate)}</td>
              <td style="padding:8px 6px;text-align:right;font-size:12px;font-weight:600;vertical-align:top;">${escapeHtml(amount)}</td>
            </tr>
          `;
        })
        .join('')
    : `<tr><td colspan="${5 + (showSku ? 1 : 0) + (showBonus ? 1 : 0)}" style="padding:16px;color:${muted};text-align:center;font-style:italic;">No line items on this purchase order.</td></tr>`;

  const totalsHtml = `
    <table role="presentation" style="margin-top:14px;width:100%;border-collapse:collapse;">
      <tr>
        <td></td>
        <td style="width:220px;">
          <table role="presentation" style="width:100%;border-collapse:collapse;border:1px solid ${border};">
            <tr>
              <td style="padding:6px 10px;font-size:12px;color:${muted};">Subtotal</td>
              <td style="padding:6px 10px;font-size:12px;text-align:right;">${escapeHtml(a.formatMoney(a.subtotal))}</td>
            </tr>
            <tr>
              <td style="padding:6px 10px;font-size:11px;color:${muted};border-top:1px solid ${border};">${escapeHtml(a.amountsAreLabel)}</td>
              <td style="padding:6px 10px;font-size:11px;color:${muted};border-top:1px solid ${border};text-align:right;"></td>
            </tr>
            <tr>
              <td style="padding:8px 10px;font-size:12px;font-weight:700;color:#ffffff;background:${accent};letterSpacing:0.5px;">TOTAL</td>
              <td style="padding:8px 10px;font-size:12px;font-weight:700;color:#ffffff;background:${accent};text-align:right;">${escapeHtml(a.formatMoney(a.totalAmount))}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `;

  const notesBlock = a.notes.trim()
    ? `<div style="margin-top:18px;"><div style="font-size:10px;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;color:${accent};margin-bottom:4px;">Notes</div><div style="font-size:12px;color:#1f2937;white-space:pre-line;">${escapeHtml(a.notes.trim())}</div></div>`
    : '';

  const termsBlock =
    a.settings.showTerms && a.settings.termsText.trim()
      ? `<div style="margin-top:14px;"><div style="font-size:10px;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;color:${accent};margin-bottom:4px;">Terms &amp; Conditions</div><div style="font-size:11px;color:${muted};white-space:pre-line;">${escapeHtml(a.settings.termsText.trim())}</div></div>`
      : '';

  const footerBlock = a.settings.footerText.trim()
    ? `<div style="margin-top:24px;padding-top:10px;border-top:1px solid ${border};font-size:10px;color:${muted};text-align:center;">${escapeHtml(a.settings.footerText.trim())}</div>`
    : '';

  return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f3f4f6;font-family:Helvetica,Arial,sans-serif;color:#0f172a;">
  <table role="presentation" style="width:100%;border-collapse:collapse;background:#f3f4f6;">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <table role="presentation" style="width:100%;max-width:640px;border-collapse:collapse;background:#ffffff;border:1px solid ${border};border-radius:6px;overflow:hidden;">
          <tr>
            <td style="padding:20px 24px;border-bottom:2px solid ${accent};">
              <div style="font-size:16px;font-weight:700;color:${accent};">${escapeHtml(a.buyer.name)}</div>
              ${a.buyer.address ? `<div style="font-size:11px;color:${muted};white-space:pre-line;margin-top:2px;">${escapeHtml(a.buyer.address)}</div>` : ''}
              <div style="font-size:11px;color:${muted};margin-top:2px;">
                ${[a.buyer.phone ? `Tel: ${escapeHtml(a.buyer.phone)}` : '', a.buyer.email ? escapeHtml(a.buyer.email) : ''].filter(Boolean).join(' &nbsp;·&nbsp; ')}
              </div>
            </td>
          </tr>
          <tr>
            <td style="padding:20px 24px;">
              ${introHtml}
              <div style="margin:18px 0;padding:8px 14px;background:${accent};color:#ffffff;font-weight:700;letter-spacing:1px;text-align:center;text-transform:uppercase;font-size:13px;">Purchase Order</div>
              <table role="presentation" style="width:100%;border-collapse:collapse;margin-bottom:16px;">
                <tr>
                  <td style="vertical-align:top;width:50%;padding-right:8px;">
                    <div style="padding:10px 12px;border:1px solid ${border};border-radius:4px;background:#fafafa;">
                      <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:${accent};margin-bottom:4px;">Supplier</div>
                      <div style="font-size:12px;font-weight:600;">${escapeHtml(a.supplier.name)}</div>
                      ${a.settings.showSupplierContact && a.supplier.contactPerson ? `<div style="font-size:11px;color:${muted};margin-top:2px;">Attn: ${escapeHtml(a.supplier.contactPerson)}</div>` : ''}
                      ${supplierContactHtml}
                    </div>
                  </td>
                  <td style="vertical-align:top;width:50%;padding-left:8px;">
                    <div style="padding:10px 12px;border:1px solid ${border};border-radius:4px;background:#fafafa;">
                      <table role="presentation" style="border-collapse:collapse;">
                        ${metaRow('PO Number', a.poNumber, accent, muted)}
                        ${metaRow('PO Date', a.poDate, accent, muted)}
                        ${a.statusLabel ? metaRow('Status', a.statusLabel, accent, muted) : ''}
                        ${metaRow('Amounts', a.amountsAreLabel, accent, muted)}
                      </table>
                    </div>
                  </td>
                </tr>
              </table>
              <table role="presentation" style="width:100%;border-collapse:collapse;">
                <thead>${headRow}</thead>
                <tbody>${bodyRows}</tbody>
              </table>
              ${totalsHtml}
              ${notesBlock}
              ${termsBlock}
            </td>
          </tr>
          ${footerBlock ? `<tr><td>${footerBlock}</td></tr>` : ''}
        </table>
      </td>
    </tr>
  </table>
</body></html>`;
}

function metaRow(label: string, value: string, accent: string, muted: string): string {
  return `
    <tr>
      <td style="padding:2px 8px 2px 0;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:${muted};">${escapeHtml(label)}</td>
      <td style="padding:2px 0;font-size:11px;color:${accent === '#5A2A18' ? '#0f172a' : accent};">${escapeHtml(value)}</td>
    </tr>
  `;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatAmountsAre(value: string | null): string {
  if (value === 'tax_inclusive') return 'Tax inclusive';
  if (value === 'tax_exclusive') return 'Tax exclusive';
  return '—';
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Minimal HTML-escape for safe embedding of user-provided strings
 * (item names, supplier name, notes, the editable intro paragraph).
 * Prevents accidental tag/attribute injection from sloppy PO content
 * per PRD.241 §15.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
